"use client";

import { useState } from "react";
import DeletePopover from "@/app/shared/delete-popover";
import PencilIcon from "@/components/icons/pencil";
import EyeIcon from "@/components/icons/eye";
import { HeaderCell } from "@/components/ui/table";
import { SortConfig } from "@/hooks/useTableServer";
import {
  calculateMonthlyPrice,
  getParamsCurrentPage,
} from "@/utilities/functions";
import { IPackage, EPackageStatus } from "@/utilities/types/Package";
import { Link } from "react-router-dom";
import { ActionIcon, Button, Modal, Tooltip } from "rizzui";
import dayjs from "dayjs";
import StatusPackage from "./status-package";
import { routes } from "@/config/routes";
import { PricingPlan } from "@/components/pricing-plans/pricing-plans";

type PackageSortBy = "name" | "price" | "status" | "createdAt";

type Columns = {
  data: IPackage[];
  sortConfig?: SortConfig<PackageSortBy>;
  onDeleteItem: (id: string) => void;
  onHeaderCellClick?: (value: string) => void;
  handleReset: () => void;
  refresh?: () => void;
  onUpdatePackage: (id: string, data: Partial<IPackage>) => Promise<void>;
};

// Package Details Modal Component
const PackageDetailsModal = ({
  isOpen,
  onClose,
  packageData,
}: {
  isOpen: boolean;
  onClose: () => void;
  packageData: IPackage;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Package Details</h2>
        <PricingPlan
          name={packageData.name}
          price={packageData.price}
          monthlyPrice={calculateMonthlyPrice(
            packageData.price,
            packageData.duration
          )}
          duration={packageData?.duration}
          description={packageData.description || ""}
          isPopular={!!packageData?.isPopular}
        />
        <div className="mt-6 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Simple view button component with its own state
const PackageViewButton = ({ packageData }: { packageData: IPackage }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <Tooltip
        size="sm"
        content={"View Package"}
        placement="top"
        color="invert"
      >
        <ActionIcon size="sm" variant="outline" onClick={openModal}>
          <EyeIcon className="h-4 w-4" />
        </ActionIcon>
      </Tooltip>

      {isModalOpen && (
        <PackageDetailsModal
          isOpen={isModalOpen}
          onClose={closeModal}
          packageData={packageData}
        />
      )}
    </>
  );
};

export const getColumns = ({
  sortConfig,
  onDeleteItem,
  onHeaderCellClick,
  onUpdatePackage,
}: Columns) => {
  return [
    {
      title: (
        <HeaderCell
          title="Name"
          sortable
          align="left"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "name"
          }
        />
      ),
      dataIndex: "name",
      key: "name",
      onHeaderCell: () => onHeaderCellClick?.("name"),
      render: (name: string) => {
        return <span className="font-medium">{name}</span>;
      },
    },

    {
      title: (
        <HeaderCell
          title="Price"
          sortable
          align="right"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "price"
          }
        />
      ),
      dataIndex: "price",
      key: "price",
      align: "right",
      onHeaderCell: () => onHeaderCellClick?.("price"),
      render: (price: number) => {
        return <span>${price}</span>;
      },
    },

    {
      title: <HeaderCell title="Duration" align="center" />,
      dataIndex: "duration",
      key: "duration",
      align: "center",
      render: (_: number, row: IPackage) => {
        if (!row.duration) return <span>-</span>;
        return (
          <span>
            {row.duration}{" "}
            {row.durationType
              ? `${row.durationType}${row.duration > 1 ? "s" : ""}`.toLowerCase()
              : `Day${row.duration > 1 ? "s" : ""}`.toLowerCase()}
          </span>
        );
      },
    },

    {
      title: (
        <HeaderCell
          title="Status"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "status"
          }
        />
      ),
      dataIndex: "status",
      key: "status",
      align: "center",
      onHeaderCell: () => onHeaderCellClick?.("status"),
      render: (_: string, row: IPackage) => {
        return (
          <StatusPackage
            onChange={async (value) => {
              await onUpdatePackage(row.id, {
                status: value as EPackageStatus,
              });
            }}
            status={row.status}
            className="w-28 text-center"
          />
        );
      },
    },
    {
      title: (
        <HeaderCell
          title="Created At"
          sortable
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
          }
        />
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      onHeaderCell: () => onHeaderCellClick?.("createdAt"),
      render: (date: string) => {
        return date ? dayjs(date).format("MMMM DD, YYYY") : "-";
      },
    },
    {
      title: (
        <HeaderCell
          className="ps-3"
          title={<span className="whitespace-nowrap">Actions</span>}
        />
      ),
      dataIndex: "action",
      key: "action",
      width: 170,
      render: (_: string, row: IPackage) => {
        return (
          <div className="flex items-center justify-end gap-3 pe-4">
            <PackageViewButton packageData={row} />

            <Tooltip
              size="sm"
              content={"Edit Package"}
              placement="top"
              color="invert"
            >
              <Link
                to={{
                  pathname: routes.package.edit(row.id),
                  search: getParamsCurrentPage({ rowKey: row?.id }),
                }}
              >
                <ActionIcon size="sm" variant="outline">
                  <PencilIcon className="h-4 w-4" />
                </ActionIcon>
              </Link>
            </Tooltip>

            <DeletePopover
              title={`Delete Package?`}
              description={`Are you sure you want to delete "${row.name}"?`}
              onDelete={() => onDeleteItem(row.id)}
            />
          </div>
        );
      },
    },
  ];
};
